import { Link } from 'react-router-dom'
import { Note } from '../api/notes'

interface NoteCardProps {
  note: Note
  onToggleStar?: (noteId: string) => void
  onToggleHome?: (noteId: string) => void
  isSelected?: boolean
  onSelect?: (noteId: string) => void
  selectionMode?: boolean
}

// Helper to strip HTML tags and truncate content
function stripHtmlAndTruncate(html: string | null, maxLength: number = 150): string {
  if (!html) return ''
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Format timestamp to readable date
function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function NoteCard({
  note,
  onToggleStar,
  onToggleHome,
  isSelected = false,
  onSelect,
  selectionMode = false,
}: NoteCardProps) {
  const preview = stripHtmlAndTruncate(note.content || note.full_content)

  return (
    <div
      data-testid="note-card"
      className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all overflow-hidden group active:scale-[0.98] touch-manipulation flex flex-col ${
        isSelected ? 'border-primary ring-2 ring-primary-100' : 'border-gray-200'
      }`}
    >
      {/* Card Header */}
      <div className="p-3 sm:p-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-start justify-between">
          {/* Checkbox for selection */}
          {(selectionMode || onSelect) && (
            <div className="mr-2 flex-shrink-0">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect?.(note.id)}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
              />
            </div>
          )}

          <Link
            to={`/notes/${note.id}`}
            className="flex-1 min-w-0"
          >
            <h3 className="font-semibold text-gray-900 truncate hover:text-primary transition-colors text-sm sm:text-base">
              {note.name || 'Untitled'}
            </h3>
          </Link>

          {/* Action buttons - always visible on mobile, hover on desktop */}
          <div className="flex items-center ml-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            {onToggleStar && (
              <button
                onClick={(e) => { e.preventDefault(); onToggleStar(note.id); }}
                className={`p-2 sm:p-1.5 rounded-lg transition-colors min-w-[36px] min-h-[36px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                  note.is_starred 
                    ? 'text-yellow-500 hover:text-yellow-600' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title={note.is_starred ? 'Remove from starred' : 'Add to starred'}
              >
                <svg className="w-5 h-5 sm:w-4 sm:h-4" fill={note.is_starred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            )}
            {onToggleHome && (
              <button
                onClick={(e) => { e.preventDefault(); onToggleHome(note.id); }}
                className={`p-2 sm:p-1.5 rounded-lg transition-colors min-w-[36px] min-h-[36px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                  note.is_home 
                    ? 'text-primary hover:text-secondary' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title={note.is_home ? 'Remove from home' : 'Add to home'}
              >
                <svg className="w-5 h-5 sm:w-4 sm:h-4" fill={note.is_home ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Tags */}
        {note.tags && note.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {note.tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
              >
                {tag.name}
              </span>
            ))}
            {note.tags.length > 3 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                +{note.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Card Body - Content Preview */}
      <Link to={`/notes/${note.id}`} className="block p-3 sm:p-4 flex-grow">
        <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
          {preview || 'No content'}
        </p>
      </Link>

      {/* Card Footer */}
      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
        <span className="text-xs text-gray-500">
          {formatDate(note.modified_time || note.created_time)}
        </span>

        <div className="flex items-center space-x-2">
          {note.source_url && (
            <a
              href={note.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-600 p-1"
              title="Open source"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
          {note.file_type === 'pdf' && (
            <span className="text-xs text-red-500 font-medium">PDF</span>
          )}
        </div>
      </div>
    </div>
  )
}
